import { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import EditableTitle from './EditableTitle';
import EditableDescription from './EditableDescription';
import ProcessSection from './ProcessSection';
import OutcomeSection from './OutcomeSection';
import ActionButtons from './ActionButtons';

interface ProcessItem { id: number; text: string; }
interface OutcomeItem { id: number; text: string; }

interface StrategySectionProps {
  title: string;
  description: string;
  processes: ProcessItem[];
  outcomes: OutcomeItem[];
  timeframe: string;
}

const StrategySection = ({ title, description, processes, outcomes, timeframe }: StrategySectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [editedDescription, setEditedDescription] = useState(description);
  const [editedProcesses, setEditedProcesses] = useState<ProcessItem[]>(processes);
  const [editedOutcomes, setEditedOutcomes] = useState<OutcomeItem[]>(outcomes);
  const [isOpen, setIsOpen] = useState(false);

  const handleSave = () => setIsEditing(false);
  const handleCancel = () => { setEditedTitle(title); setEditedDescription(description); setEditedProcesses(processes); setEditedOutcomes(outcomes); setIsEditing(false); };
  const updateProcess = (id: number, newText: string) => setEditedProcesses(prev => prev.map(p => p.id === id ? { ...p, text: newText } : p));
  const updateOutcome = (id: number, newText: string) => setEditedOutcomes(prev => prev.map(o => o.id === id ? { ...o, text: newText } : o));
  const handleCardClick = () => { if (!isEditing) setIsOpen(!isOpen); };

  return (
    <Card className="bg-white/5 border border-white/10 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md shadow-sm" onClick={handleCardClick}>
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-4">
          <EditableTitle title={editedTitle} isEditing={isEditing} isOpen={isOpen} onTitleChange={setEditedTitle} />
        </div>
        <EditableDescription description={editedDescription} isEditing={isEditing} onDescriptionChange={setEditedDescription} />
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleContent>
            <div className="space-y-6 pt-2">
              <ProcessSection processes={editedProcesses} isEditing={isEditing} onProcessUpdate={updateProcess} />
              <OutcomeSection outcomes={editedOutcomes} isEditing={isEditing} onOutcomeUpdate={updateOutcome} />
              <ActionButtons timeframe={timeframe} isEditing={isEditing} onSave={handleSave} onCancel={handleCancel} />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

export default StrategySection;
